import { logger } from '@/utils/logger/logger';

/**
 * Patches DOM methods to handle Google Translate and other third-party libraries
 * that manipulate the DOM and may attempt to remove/insert nodes incorrectly.
 *
 * This fixes the error: "Failed to execute 'removeChild' on 'Node':
 * The node to be removed is not a child of this node."
 */
export default function applyGoogleTranslateDOMPatch() {
  if (typeof Node === 'function' && Node.prototype) {
    // eslint-disable-next-line @typescript-eslint/unbound-method
    const originalRemoveChild = Node.prototype.removeChild;

    Node.prototype.removeChild = function <T extends Node>(child: T): T {
      if (child.parentNode !== this) {
        logger.warn(
          'Google Translate attempted to remove a child node from the wrong parent. Skipping.',
          {
            childNode: child?.nodeName,
            expectedParent: this?.nodeName,
            actualParent: child?.parentNode?.nodeName,
          }
        );
        return child;
      }

      // eslint-disable-next-line @typescript-eslint/unbound-method, prefer-rest-params, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any
      return originalRemoveChild.apply(this, arguments as any) as T;
    };

    // eslint-disable-next-line @typescript-eslint/unbound-method
    const originalInsertBefore = Node.prototype.insertBefore;

    Node.prototype.insertBefore = function <T extends Node>(
      newNode: T,
      referenceNode: Node | null
    ): T {
      if (referenceNode && referenceNode.parentNode !== this) {
        const details = {
          newNode: newNode?.nodeName,
          expectedParent: this?.nodeName,
          actualParent: referenceNode?.parentNode?.nodeName,
        };

        // Detached inserts (Amazon pixel <script>) still need to land.
        // Already-attached children are React reorders whose reference
        // Google Translate moved — appendChild would yank them to lastChild.
        if (newNode.parentNode == null) {
          try {
            logger.warn(
              'Google Translate attempted to insert before a reference node from the wrong parent. Appending instead.',
              details
            );
            return this.appendChild(newNode);
          } catch {
            logger.warn(
              'Google Translate attempted to insert before a reference node from the wrong parent. Skipping.',
              details
            );
            return newNode;
          }
        }

        logger.warn(
          'Google Translate attempted to insert before a reference node from the wrong parent. Skipping.',
          details
        );
        return newNode;
      }

      // eslint-disable-next-line @typescript-eslint/unbound-method, prefer-rest-params, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any
      return originalInsertBefore.apply(this, arguments as any) as T;
    };
  }
}
